
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import Header from "../Header/Header.js";
import Footer from "../Footer/Footer.js";
import { MdMailOutline, MdSupportAgent } from "react-icons/md";
import { CREATE_INQUIRY } from "../graphql/Mutation/FranchisseInquiry.js";
import { useMutation } from "@apollo/client";
import { toast } from "react-toastify";
import SpinLoader from "../components/SpinLoader.js";
import useScrollToTop from "../components/useScrollToTop.js";

const FranchiseeEnquiry = () => {

    useScrollToTop();


    const [createInquiry, { loading }] = useMutation(CREATE_INQUIRY);

    // Validation schema using Yup
    const validationSchema = Yup.object({
        fullName: Yup.string().required("Full Name is required"),
        contactNo: Yup.string()
            .required("Contact Number is required")
            .matches(/^[0-9]{10}$/, "Enter a valid 10-digit number"),
        preferredArea: Yup.string().required("Preferred Area is required"),
        pincode: Yup.string()
            .required("Pincode is required")
            .matches(/^[0-9]{6}$/, "Enter a valid 6-digit pincode"),
        city: Yup.string().required("City is required"),
        message: Yup.string(),
    });

    // Form submit handler
    const handleSubmit = async (values, { resetForm }) => {
        try {
            const { data } = await createInquiry({ variables: { input: values } });
            if (data?.createFranchiseInquiry?.id) {
                toast.success("Thank you! Your enquiry has been submitted.");
                resetForm();
            }
        } catch (error) {
            // console.log('✌️error --->', error);
            const message = error?.graphQLErrors?.[0]?.message || error?.message;
            toast.error(message || "Error occurred");
        }
    };
    return (
        <>
            <Header />
            <div className="p-6 bg-white shadow-md rounded-lg">
                <h2 className="text-lg font-semibold mb-4">
                    Become a Franchise Partner
                </h2>
                <p className="text-sm text-slate-600 mb-6">Join our growing network and take the first step towards a rewarding business opportunity. Fill out the form below to express your interest in becoming a franchise partner. Our team will get in touch with you to guide you through the next steps.</p>
                <Formik
                    initialValues={{
                        fullName: "",
                        contactNo: "",
                        preferredArea: "",
                        pincode: "",
                        city: "",
                        message: "",
                    }}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                >
                    {() => (
                        <Form className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 p-6 border border-slate-200 rounded-md gap-6">

                            {/* Full Name */}
                            <div className="">
                                <label className="block font-medium mb-1">
                                    Full Name <span className="text-red-600">*</span>
                                </label>
                                <Field
                                    name="fullName"
                                    type="text"
                                    className="w-full border border-gray-400 rounded px-3 py-2 focus:outline-none"
                                    placeholder="Enter your full name"
                                />
                                <ErrorMessage
                                    name="fullName"
                                    component="div"
                                    className="text-red-500 text-sm mt-1"
                                />
                            </div>

                            {/* Contact Number */}
                            <div className="">
                                <label className="block font-medium mb-1">
                                    Contact Number <span className="text-red-600">*</span>
                                </label>
                                <Field
                                    name="contactNo"
                                    type="text"
                                    className="w-full border border-gray-400 rounded px-3 py-2 focus:outline-none"
                                    placeholder="Enter 10-digit contact number"
                                />
                                <ErrorMessage
                                    name="contactNo"
                                    component="div"
                                    className="text-red-500 text-sm mt-1"
                                />
                            </div>

                            {/* Preferred Area */}
                            <div className="">
                                <label className="block font-medium mb-1">
                                    Preferred Area <span className="text-red-600">*</span>
                                </label>
                                <Field
                                    name="preferredArea"
                                    type="text"
                                    className="w-full border border-gray-400 rounded px-3 py-2 focus:outline-none"
                                    placeholder="Enter preferred area for franchise"
                                />
                                <ErrorMessage
                                    name="preferredArea"
                                    component="div"
                                    className="text-red-500 text-sm mt-1"
                                />
                            </div>

                            {/* Pincode */}
                            <div className="">
                                <label className="block font-medium mb-1">
                                    Pincode <span className="text-red-600">*</span>
                                </label>
                                <Field
                                    name="pincode"
                                    type="text"
                                    className="w-full border border-gray-400 rounded px-3 py-2 focus:outline-none"
                                    placeholder="Enter area pincode"
                                />
                                <ErrorMessage
                                    name="pincode"
                                    component="div"
                                    className="text-red-500 text-sm mt-1"
                                />
                            </div>

                            {/* City */}
                            <div className="">
                                <label className="block font-medium mb-1">
                                    City <span className="text-red-600">*</span>
                                </label>
                                <Field
                                    name="city"
                                    type="text"
                                    className="w-full border border-gray-400 rounded px-3 py-2 focus:outline-none"
                                    placeholder="Enter city name"
                                />
                                <ErrorMessage
                                    name="city"
                                    component="div"
                                    className="text-red-500 text-sm mt-1"
                                />
                            </div>

                            {/* Message */}
                            <div className="col-span-1 md:col-span-2 lg:col-span-2">
                                <label className="block font-medium mb-1">Message (Optional)</label>
                                <Field
                                    name="message"
                                    as="textarea"
                                    rows="4"
                                    className="w-full border border-gray-400 rounded px-3 py-2 focus:outline-none"
                                    placeholder="Write your message here"
                                />
                            </div>

                            {/* Submit Button */}
                            <div className="col-span-1 md:col-span-2 lg:col-span-4 mt-6">
                                <button
                                    type="submit"
                                    className="w-full bg-slate-700 text-white py-2 rounded hover:bg-slate-800 transition duration-300 font-medium"
                                >
                                    Apply for Franchise {loading && <SpinLoader />}
                                </button>
                            </div>

                        </Form>
                    )}
                </Formik>


                <div className="mt-4 bg-white border rounded-md p-4">
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">We're here to help</h3>
                    <p className="text-sm text-gray-600 mb-6">Have a question or need assistance? Reach out to our support team and we'll get back to you promptly.</p>


                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {/* Raise a Query */}
                        <a

                            href="mailto:algarxpress@gmail.com"
                            className="flex items-center p-4 bg-blue-50 rounded-xl hover:shadow-md transition"
                        >
                            <div className="flex-shrink-0 flex items-center justify-center mr-3">
                                <MdMailOutline className="h-7 w-7 text-slate-600" />
                            </div>
                            <div>
                                <p className="text-xs text-gray-500">Raise a Query</p>
                                <p className="text-sm font-medium text-slate-800">algarxpress@gmail.com</p>
                            </div>
                        </a>



                        {/* Customer Care */}
                        <a href="tel:022 6962 2461" className="flex items-center p-4 bg-blue-50 rounded-xl hover:shadow-md transition">
                            <div className="flex-shrink-0 flex items-center justify-center mr-3">
                                <MdSupportAgent className="h-7 w-7 text-slate-600" />
                            </div>
                            <div>
                                <p className="text-xs text-gray-500">Customer Care</p>
                                <p className="text-sm font-medium text-gray-800">(+91) 022 6962 2461</p>
                            </div>
                        </a>
                    </div>


                    {/* <p className="text-xs text-gray-400 mt-5">Available Mon - Sat, 9:00 AM - 7:00 PM</p> */}
                </div>
            </div>

            <Footer />
        </>
    );
};

export default FranchiseeEnquiry;
